/* eslint-disable react/display-name */

import sum from 'lodash/sum';
import React, { Fragment, PureComponent } from 'react';
import 'react-table/react-table.css';

import Expander from './components/Expander';
import ControlsPanel from './components/ControlsPanel';
import Tooltip from './components/Tooltip';
import Table from './components/Table';

import { toFloat, toggleBodyClass } from './helpers';

/**
 * Table header rendering
 * @param {string} title Column title
 * @param {string} description Column description
 */
const renderHeader = (title, description) => (
  <Tooltip text={description}>{title}</Tooltip>
);

/**
 * Render cell value and difference with statistic value
 *
 * @param {string} realValue Real value
 * @param {string} expectedValue Expected value
 */
const renderCellWithDifference = (realValue, expectedValue) => {
  const difference = parseInt(realValue, 10) - parseFloat(expectedValue);
  const isHigherThanExpected = difference > 0;

  const differenceLabel = (
    <sup className={`xDifference ${isHigherThanExpected ? 'higher' : 'lower'}`}>
      {isHigherThanExpected ? `-` : `+`}
      {Math.abs(difference).toFixed(2)}
    </sup>
  );

  return (
    <Fragment>
      {toFloat(expectedValue)}
      {difference ? differenceLabel : ''}
    </Fragment>
  );
};

/**
 * Render footer with difference label
 *
 * @param {Array} data Table data
 * @param {string} originalField Regular field name
 * @param {string} xField xStat field name
 */
const renderFooterWithDifference = (data, originalField, xField) => {
  const values = data.reduce(
    (total, current) => {
      return {
        [originalField]:
          total[originalField] + parseFloat(current[originalField]),
        [xField]: total[xField] + parseFloat(current[xField]),
      };
    },
    {
      [originalField]: 0,
      [xField]: 0,
    }
  );

  return renderCellWithDifference(values[originalField], values[xField]);
};

/**
 * Table columns
 */
const columnsParams = [
  {
    Header: 'Игрок',
    accessor: 'name',
    width: 150,
  },
  {
    Header: 'Игры',
    accessor: 'games',
  },
  {
    Header: 'Мин.',
    accessor: 'time',
  },
  {
    Header: renderHeader('G', 'Голы'),
    accessor: 'goals',
    Footer: column =>
      sum(column.data.map(player => parseInt(player.goals, 10))),
  },
  {
    Header: renderHeader('A', 'Голевые передачи'),
    accessor: 'assists',
    Footer: column =>
      sum(column.data.map(player => parseInt(player.assists, 10))),
  },
  {
    Header: renderHeader('NPG', 'Голы не с пенальти'),
    accessor: 'npg',
    Footer: column => sum(column.data.map(player => parseInt(player.npg, 10))),
  },
  {
    Header: renderHeader('xG', 'Ожидаемые голы'),
    accessor: 'xG',
    Cell: ({ original: { goals: originalValue }, value }) =>
      renderCellWithDifference(originalValue, value),
    Footer: ({ data }) => renderFooterWithDifference(data, 'goals', 'xG'),
  },
  {
    Header: renderHeader('xA', 'Ожидаемые голевые передачи'),
    accessor: 'xA',
    Cell: ({ original: { assists: originalValue }, value }) =>
      renderCellWithDifference(originalValue, value),
    Footer: ({ data }) => renderFooterWithDifference(data, 'assists', 'xA'),
  },
  {
    Header: renderHeader('NPxG', 'Ожидаемые голы не с пенальти'),
    accessor: 'npxG',
    Cell: row => toFloat(row.value),
    Footer: column =>
      sum(column.data.map(player => parseFloat(player.npxG || 0))).toFixed(2),
  },
  {
    id: 'xg90',
    Header: renderHeader(
      'xG90',
      'Среднее ожидаемое количество голов за 90 минут'
    ),
    accessor: v => (v.xG / v.time) * 90,
    Cell: row => toFloat(row.value),
    Footer: column =>
      sum(column.data.map(player => parseFloat(player.xg90 || 0))).toFixed(2),
  },
  {
    id: 'xa90',
    Header: renderHeader(
      'xA90',
      'Среднее ожидаемое количество голевых передач за 90 минут'
    ),
    accessor: v => (v.xA / v.time) * 90,
    Cell: row => toFloat(row.value),
    Footer: column =>
      sum(column.data.map(player => parseFloat(player.xa90 || 0))).toFixed(2),
  },
  {
    id: 'shots',
    Header: renderHeader('Sh90', 'Среднее количество ударов за 90 минут'),
    accessor: v => (v.shots / v.time) * 90,
    Cell: row => toFloat(row.value),
    Footer: column =>
      sum(column.data.map(player => parseFloat(player.shots || 0))).toFixed(2),
  },
  {
    id: 'kp',
    Header: renderHeader(
      'KP90',
      'Среднее количество ключевых передач за 90 минут'
    ),
    accessor: v => (v.key_passes / v.time) * 90,
    Cell: row => toFloat(row.value),
    Footer: column =>
      sum(column.data.map(player => parseFloat(player.kp || 0))).toFixed(2),
  },
  {
    Header: renderHeader(
      'xGChain',
      'xG всех атак, которые завершились ударом и в которых игрок принимал участие. Значение за 90 минут.'
    ),
    accessor: 'xGChain',
    Cell: row => toFloat(row.value),
    Footer: column =>
      sum(column.data.map(player => parseFloat(player.xGChain || 0))).toFixed(
        2
      ),
  },
  {
    Header: renderHeader(
      'xGBuildup',
      'xG всех атак, которые завершились ударом и в которых игрок принимал участие, без начисления балов за голы и ключевые пасы. Значение за 90 минут.'
    ),
    accessor: 'xGBuildup',
    Cell: row => toFloat(row.value),
    Footer: column =>
      sum(column.data.map(player => parseFloat(player.xGBuildup || 0))).toFixed(
        2
      ),
  },
];

class App extends PureComponent {
  state = {
    isExpanded: false,
    activeColumns: {
      name: true,
      games: true,
      time: true,
      goals: true,
      assists: true,
      npg: false,
      xG: true,
      xA: true,
      npxG: false,
      xg90: true,
      xa90: true,
      shots: false,
      kp: false,
      xGChain: false,
      xGBuildup: false,
    },
  };

  clickHandler = e => {
    const { key } = e.target.dataset;

    this.setState(state => ({
      activeColumns: {
        ...state.activeColumns,
        [key]: !state.activeColumns[key],
      },
    }));
  };

  expandClickHandler = () => {
    this.setState(
      state => ({ isExpanded: !state.isExpanded }),
      () => {
        toggleBodyClass();
      }
    );
  };

  render() {
    const { activeColumns, isExpanded } = this.state;

    const className = ['xgTable-wrapper', isExpanded ? 'expanded' : ''].join(
      ' '
    );

    return (
      <div className={className}>
        <ControlsPanel
          activeColumns={activeColumns}
          columns={columnsParams}
          onClick={this.clickHandler}
        />
        <Expander
          isExpanded={isExpanded}
          activeColumns={activeColumns}
          onClick={this.expandClickHandler}
        />
        <Table activeColumns={activeColumns} columns={columnsParams} />
      </div>
    );
  }
}

export default App;
